import clsx from 'clsx';

/**
 * Mana Home Loans logo.
 *
 * Two display modes:
 *  - emblem: just the house icon (square SVG)
 *  - full: house + wordmark + NMLS line, stacked
 *
 * Color scheme is controlled by the `tone` prop so the logo can sit on either
 * the cream/light background or the navy sidebar.
 *
 * To swap in the official PNG asset, drop it at `apps/web/public/logo.png` and
 * pass `usePng` to render `<img src="/logo.png" ... />` instead of the SVG.
 */
export function Logo({
  variant = 'emblem',
  tone = 'navy',
  size = 40,
  className,
}: {
  variant?: 'emblem' | 'full';
  tone?: 'navy' | 'light';
  size?: number;
  className?: string;
}) {
  const textColor =
    tone === 'navy' ? 'text-white' : 'text-brand-navy-800';
  const subColor = tone === 'navy' ? 'text-brand-gold-200' : 'text-brand-gold-600';
  const mullion = tone === 'navy' ? '#142A45' : '#FAF8F3';
  const window = tone === 'navy' ? '#0C1B30' : '#1B3A5C';

  const emblem = (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      role="img"
      aria-label="Mana Home Loans"
      className={clsx('shrink-0', className)}
    >
      <path d="M32 6 L58 36 H6 Z" fill="#B8965A" />
      <rect x="26" y="22" width="12" height="12" fill={window} />
      <line x1="32" y1="22" x2="32" y2="34" stroke={mullion} strokeWidth="1.5" />
      <line x1="26" y1="28" x2="38" y2="28" stroke={mullion} strokeWidth="1.5" />
    </svg>
  );

  if (variant === 'emblem') return emblem;

  return (
    <div className={clsx('flex items-center gap-3', className)}>
      {emblem}
      <div className="leading-tight">
        <div className={clsx('font-display text-lg font-bold tracking-tight', textColor)}>
          Mana Home Loans
        </div>
        <div className={clsx('text-[10px] font-medium uppercase tracking-[0.18em]', subColor)}>
          NMLS#2701164
        </div>
      </div>
    </div>
  );
}
